import graphql from 'babel-plugin-relay/macro'
import { lazy, Suspense, useEffect } from 'react'
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAppManagementContext } from 'scenes/appManagement/context'

import { AppMongoRouterQuery } from './__generated__/AppMongoRouterQuery.graphql'

const AppMongoConnectorScene = lazy(() => import('./AppMongoConnectorScene'))
const ConfigureMongoConnectorScene = lazy(() => import('./configure'))

const query = graphql`
  query AppMongoRouterQuery($appId: ID!) {
    viewer {
      appById(id: $appId) {
        # eslint-disable-next-line relay/unused-fields
        id
        viewerCanManageMongoConnector
        mongoConnector {
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...MongoConnectorDetails_link
        }
      }
    }
  }
`

type Props = {
  queryRef: PreloadedQuery<AppMongoRouterQuery>
}

const AppMongoRouter = ({ queryRef }: Props) => {
  const {
    viewer: { appById: app },
  } = usePreloadedQuery(query, queryRef)
  const canConfigure = !!app?.viewerCanManageMongoConnector

  return (
    <Routes>
      <Route path="/" element={<AppMongoConnectorScene />} />
      {canConfigure && (
        <Route
          path="/configure"
          element={
            <ConfigureMongoConnectorScene fragment={app.mongoConnector} />
          }
        />
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function AppMongoRouterContainer() {
  const [queryRef, loadQuery] = useQueryLoader<AppMongoRouterQuery>(query)

  const { appId } = useAppManagementContext()
  useEffect(() => {
    if (appId) {
      loadQuery({ appId }, { fetchPolicy: 'store-and-network' })
    }
  }, [loadQuery, appId])

  if (!queryRef) {
    return null
  }

  return (
    <Suspense>
      <AppMongoRouter queryRef={queryRef} />
    </Suspense>
  )
}
